import { getSupabaseClient } from '@/integrations/supabase/client';
import { OrganizationMessage } from '@/types';

const DbMessage = (message: any): OrganizationMessage => ({
  id: message.id,
  organizationId: message.organization_id,
  messageText: message.message_text,
  expiresAt: new Date(message.expires_at),
  isActive: message.is_active,
  isUrgent: message.is_urgent || false, 
  createdBy: message.created_by,
  createdAt: new Date(message.created_at),
  updatedAt: new Date(message.updated_at)
});

export const messagesService = {
  async getActiveMessages(organizationId: string): Promise<OrganizationMessage[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('organization_messages')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data.map(DbMessage);
  },

  async getAllMessages(organizationId: string): Promise<OrganizationMessage[]> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('organization_messages')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data.map(DbMessage);
  },

  async createMessage(
    organizationId: string,
    messageText: string,
    expiresAt: Date,
    createdBy: string,
    isActive: boolean = true,
    isUrgent: boolean = false
  ): Promise<OrganizationMessage> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('organization_messages')
      .insert({
        organization_id: organizationId,
        message_text: messageText,
        expires_at: expiresAt.toISOString(),
        created_by: createdBy,
        is_active: isActive,
        is_urgent: isUrgent
      })
      .select()
      .single();
    
    if (error) throw error;
    
    return DbMessage(data);
  },

  async updateMessage(
    messageId: string,
    messageText: string,
    expiresAt: Date,
    isActive: boolean,
    isUrgent: boolean = false
  ): Promise<OrganizationMessage> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('organization_messages')
      .update({
        message_text: messageText,
        expires_at: expiresAt.toISOString(),
        is_active: isActive,
        is_urgent: isUrgent,
        updated_at: new Date().toISOString()
      })
      .eq('id', messageId)
      .select()
      .single();
    
    if (error) throw error;
    
    return DbMessage(data);
  },

  async deleteMessage(messageId: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('organization_messages')
      .delete()
      .eq('id', messageId);
    
    if (error) throw error;
  },

  async toggleMessageStatus(messageId: string, isActive: boolean): Promise<OrganizationMessage> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('organization_messages')
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .eq('id', messageId)
      .select()
      .single();
    
    if (error) throw error;
    
    return DbMessage(data);
  }
};
